import { currencyFormat } from 'helpers/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface OrderSummaryDetailsProps {
  orderDetails?: {
    items?: any[];
    total_seller_amount?: number;
    tax?: number;
    discount?: number;
    shipping_cost?: number;
  };
}

const OrderSummaryDetails = ({ orderDetails }: OrderSummaryDetailsProps) => {
  const { t } = useTranslation();
  // Calculate items subtotal, total quantities, commission and net earnings
  const calculateStats = () => {
    if (!orderDetails?.items || orderDetails.items.length === 0) {
      return {
        subtotal: 0,
        totalCommission: 0,
        totalNetEarnings: 0,
        totalOrdered: 0,
        totalInvoiced: 0,
        totalShipped: 0,
        totalRefunded: 0
      };
    }
    
    let subtotal = 0;
    let totalCommission = 0;
    let totalNetEarnings = 0;
    let totalOrdered = 0;
    let totalInvoiced = 0;
    let totalShipped = 0;
    let totalRefunded = 0;
    
    orderDetails.items.forEach(item => {
      // Financial calculations
      subtotal += (item.total_price || 0);
      totalCommission += (item.comission || 0);
      totalNetEarnings += (item.actual_seller_amount || 0);
      
      // Quantity calculations
      totalOrdered += (item.qty_commanded || 0);
      totalInvoiced += (item.qty_invoiced || 0);
      totalShipped += (item.qty_shipped || 0);
      totalRefunded += (item.qty_refunded || 0);
    });
    
    return {
      subtotal,
      totalCommission,
      totalNetEarnings,
      totalOrdered,
      totalInvoiced,
      totalShipped,
      totalRefunded
    };
  };
  
  const stats = calculateStats();
  
  // Default values or values from API
  const tax = orderDetails?.tax || 0;
  const discount = orderDetails?.discount || 0;
  const shipping = orderDetails?.shipping_cost || 0;
  
  // Final total (use API total if available, otherwise calculate)
  const total = orderDetails?.total_seller_amount || (stats.subtotal - discount + tax + shipping);
  
  return (
    <div className="px-2 py-1">
      <div className="d-flex justify-content-between mb-3">
        <span className="text-body fw-semibold" style={{ fontSize: '1rem' }}>{t('orderSummary.itemsSubtotal')}:</span>
        <span className="text-body-emphasis fw-semibold" style={{ fontSize: '1rem' }}>{currencyFormat(stats.subtotal)}</span>
      </div>
      
      {discount > 0 && (
        <div className="d-flex justify-content-between mb-3">
          <span className="text-body fw-semibold" style={{ fontSize: '1rem' }}>{t('orderSummary.discount')}:</span>
          <span className="text-danger fw-semibold" style={{ fontSize: '1rem' }}>-{currencyFormat(discount)}</span>
        </div>
      )}
      
      {tax > 0 && (
        <div className="d-flex justify-content-between mb-3">
          <span className="text-body fw-semibold" style={{ fontSize: '1rem' }}>{t('orderSummary.tax')}:</span>
          <span className="text-body-emphasis fw-semibold" style={{ fontSize: '1rem' }}>{currencyFormat(tax)}</span>
        </div>
      )}
      
      {shipping > 0 && (
        <div className="d-flex justify-content-between mb-3">
          <span className="text-body fw-semibold" style={{ fontSize: '1rem' }}>{t('orderSummary.shipping')}:</span>
          <span className="text-body-emphasis fw-semibold" style={{ fontSize: '1rem' }}>{currencyFormat(shipping)}</span>
        </div>
      )}
      
      <div className="border-top border-dashed pt-3 mt-2">
        <div className="d-flex justify-content-between mb-3">
          <span className="text-body fw-semibold" style={{ fontSize: '1rem' }}>{t('orderSummary.commission')}:</span>
          <span className="text-body-tertiary fw-semibold" style={{ fontSize: '1rem' }}>{currencyFormat(stats.totalCommission)}</span>
        </div>
        
        <div className="d-flex justify-content-between mb-3">
          <span className="text-body fw-semibold" style={{ fontSize: '1rem' }}>{t('orderSummary.netEarnings')}:</span>
          <span className="text-success fw-bold" style={{ fontSize: '1rem' }}>{currencyFormat(stats.totalNetEarnings)}</span>
        </div>
      </div>
      
      <div className="border-top pt-3 mt-2">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <span className="text-body-secondary mb-0 fst-italic" style={{ fontSize: '0.95rem' }}>{t('orderSummary.quantities')}:</span>
        </div>
        <div className="d-flex justify-content-between mb-2">
          <span className="text-body-secondary" style={{ fontSize: '0.95rem' }}>
            <span className="fw-semibold">{t('orderSummary.ordered')}:</span> 
            <span className="ms-1">{stats.totalOrdered}</span>
          </span>
          <span className="text-body-secondary" style={{ fontSize: '0.95rem' }}>
            <span className="fw-semibold">{t('orderSummary.invoiced')}:</span> 
            <span className="ms-1">{stats.totalInvoiced}</span>
          </span>
        </div>
        <div className="d-flex justify-content-between">
          <span className="text-body-secondary" style={{ fontSize: '0.95rem' }}>
            <span className="fw-semibold">{t('orderSummary.shipped')}:</span> 
            <span className="ms-1">{stats.totalShipped}</span>
          </span>
          <span className="text-body-secondary" style={{ fontSize: '0.95rem' }}>
            <span className="fw-semibold">{t('orderSummary.refunded')}:</span> 
            <span className="ms-1">{stats.totalRefunded}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default OrderSummaryDetails;
